import { useState, useEffect, useCallback } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "../App";
import { toast } from "sonner";
import { ArrowLeft, NotebookPen, Plus, LogOut, UserPlus, LogIn, BookPlus, Edit3, Trash2, Check, X, ArchiveRestore } from "lucide-react";
import { TradeTable, OUTCOMES, DIRECTIONS, EMOTIONS } from "../components/TradeTable";
import { JournalChart } from "../components/JournalChart";
import { WhatIf } from "../components/WhatIf";
import { PeriodReview } from "../components/PeriodReview";

const TOKEN_KEY = "gv_journal_token";

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const emptyTrade = () => ({ trade_datetime: nowLocal(), pair: "XAUUSD", direction: "buy", screenshot_url: "", outcome: "win", percentage: "", emotions: [], notes: "" });

export default function TradingJournal() {
  const [jToken, setJToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [jUser, setJUser] = useState(null);
  const [books, setBooks] = useState([]);
  const [activeBookId, setActiveBookId] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [authError, setAuthError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newTrade, setNewTrade] = useState(emptyTrade());
  const [showNewBook, setShowNewBook] = useState(false);
  const [newBookName, setNewBookName] = useState("");
  const [renamingBook, setRenamingBook] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);

  const jHeaders = useCallback((token) => ({ headers: { "X-Journal-Token": token } }), []);

  const journalLogout = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      axios.post(`${API}/journal/trash/empty`, {}, { headers: { "X-Journal-Token": token } }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    setJToken(null);
    setJUser(null);
    setBooks([]);
    setActiveBookId(null);
    setTrades([]);
    setTrash([]);
    setShowTrash(false);
    setForm({ email: "", password: "", first_name: "", last_name: "" });
    setMode("login");
    setAuthError("");
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!jToken) return;
    (async () => {
      try {
        const me = await axios.get(`${API}/journal/me`, jHeaders(jToken));
        setJUser(me.data);
        const bks = await axios.get(`${API}/journal/books`, jHeaders(jToken));
        setBooks(bks.data);
        setActiveBookId((prev) => prev && bks.data.some(b => b.id === prev) ? prev : (bks.data[0]?.id || null));
      } catch {
        journalLogout();
      } finally {
        setLoading(false);
      }
    })();
  }, [jToken, jHeaders, journalLogout]);

  useEffect(() => {
    if (!jToken || !activeBookId) { setTrades([]); setTrash([]); return; }
    axios.get(`${API}/journal/trades?journal_id=${activeBookId}`, jHeaders(jToken))
      .then((res) => setTrades(res.data))
      .catch(() => setTrades([]));
    axios.get(`${API}/journal/trades/trash?journal_id=${activeBookId}`, jHeaders(jToken))
      .then((res) => setTrash(res.data))
      .catch(() => setTrash([]));
  }, [jToken, activeBookId, jHeaders]);

  const refreshBooks = async () => {
    const bks = await axios.get(`${API}/journal/books`, jHeaders(jToken));
    setBooks(bks.data);
    return bks.data;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    setSubmitting(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const res = await axios.post(`${API}/journal/${mode === "login" ? "login" : "register"}`, payload);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setJToken(res.data.token);
      setLoading(true);
      toast.success(mode === "login" ? "Bentornato nel tuo Journal!" : "Profilo Journal creato!");
    } catch (err) {
      setAuthError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const createBook = async () => {
    if (!newBookName.trim()) { toast.error("Inserisci un nome"); return; }
    try {
      const res = await axios.post(`${API}/journal/books`, { name: newBookName }, jHeaders(jToken));
      setBooks((prev) => [...prev, res.data]);
      setActiveBookId(res.data.id);
      setNewBookName("");
      setShowNewBook(false);
      toast.success("Journal creato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const renameBook = async () => {
    if (!renameValue.trim()) return;
    try {
      await axios.put(`${API}/journal/books/${activeBookId}`, { name: renameValue }, jHeaders(jToken));
      setBooks((prev) => prev.map((b) => (b.id === activeBookId ? { ...b, name: renameValue } : b)));
      setRenamingBook(false);
      toast.success("Rinominato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteBook = async () => {
    const book = books.find((b) => b.id === activeBookId);
    if (!window.confirm(`Eliminare il journal "${book?.name}" e tutti i suoi trade?`)) return;
    try {
      await axios.delete(`${API}/journal/books/${activeBookId}`, jHeaders(jToken));
      const bks = await refreshBooks();
      setActiveBookId(bks[0]?.id || null);
      toast.success("Journal eliminato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addTrade = async () => {
    try {
      const payload = { ...newTrade, journal_id: activeBookId, percentage: newTrade.percentage === "" ? 0 : parseFloat(newTrade.percentage) };
      const res = await axios.post(`${API}/journal/trades`, payload, jHeaders(jToken));
      setTrades((prev) => [res.data, ...prev].sort((a, b) => (b.trade_datetime || "").localeCompare(a.trade_datetime || "")));
      setBooks((prev) => prev.map((b) => (b.id === activeBookId ? { ...b, trade_count: (b.trade_count || 0) + 1 } : b)));
      setNewTrade(emptyTrade());
      setShowAdd(false);
      toast.success("Trade aggiunto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateTrade = async (id, field, value) => {
    try {
      const res = await axios.put(`${API}/journal/trades/${id}`, { [field]: value }, jHeaders(jToken));
      setTrades((prev) => prev.map((t) => (t.id === id ? res.data : t)));
      toast.success("Salvato", { duration: 1200 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteTrade = async (id) => {
    try {
      await axios.delete(`${API}/journal/trades/${id}`, jHeaders(jToken));
      const trade = trades.find((t) => t.id === id);
      setTrades((prev) => prev.filter((t) => t.id !== id));
      if (trade) setTrash((prev) => [trade, ...prev]);
      setBooks((prev) => prev.map((b) => (b.id === activeBookId ? { ...b, trade_count: Math.max((b.trade_count || 1) - 1, 0) } : b)));
      toast.success("Spostato nel cestino: puoi ripristinarlo fino al logout");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restoreTrade = async (id) => {
    try {
      const res = await axios.post(`${API}/journal/trades/${id}/restore`, {}, jHeaders(jToken));
      setTrash((prev) => prev.filter((t) => t.id !== id));
      setTrades((prev) => [res.data, ...prev].sort((a, b) => (b.trade_datetime || "").localeCompare(a.trade_datetime || "")));
      setBooks((prev) => prev.map((b) => (b.id === activeBookId ? { ...b, trade_count: (b.trade_count || 0) + 1 } : b)));
      toast.success("Trade ripristinato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const wins = trades.filter((t) => t.outcome === "win").length;
  const losses = trades.filter((t) => t.outcome === "loss").length;
  const winRate = wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : null;
  const displayName = jUser ? `${jUser.first_name || ""} ${jUser.last_name || ""}`.trim() || jUser.email : "";

  return (
    <div className="page-container" data-testid="trading-journal-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="Strumenti" title="Trading Journal" subtitle="Il tuo diario di trading privato: registra, analizza e migliora" />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        <Link to="/strumenti" className="inline-flex items-center gap-2 mb-8 text-sm font-semibold" style={{ color: "var(--mm-gold)" }} data-testid="back-to-strumenti">
          <ArrowLeft size={16} /> Torna agli Strumenti
        </Link>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
          </div>
        ) : !jUser ? (
          <div className="max-w-md mx-auto glass-card p-8 sm:p-10" data-testid="journal-auth-card">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "rgba(47,216,168,0.12)" }}>
              <NotebookPen size={26} style={{ color: "var(--mm-gold)" }} />
            </div>
            <h2 className="text-lg font-bold text-center mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
              {mode === "login" ? "Accedi al tuo Journal" : "Crea il tuo profilo Journal"}
            </h2>
            <p className="text-sm text-center mb-7" style={{ color: "var(--mm-text-muted)" }}>
              I tuoi trade sono privati: solo tu e l'admin potete vederli.
            </p>
            <form onSubmit={handleAuth} className="space-y-4">
              {mode === "register" && (
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" required placeholder="Nome" value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="admin-input" data-testid="journal-firstname-input" />
                  <input type="text" required placeholder="Cognome" value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="admin-input" data-testid="journal-lastname-input" />
                </div>
              )}
              <input type="email" required placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="admin-input" data-testid="journal-email-input" />
              <input type="password" required placeholder="Password (min 6 caratteri)" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="admin-input" data-testid="journal-password-input" />
              {authError && <p className="text-sm" style={{ color: "#EF4444" }} data-testid="journal-auth-error">{authError}</p>}
              <button type="submit" disabled={submitting} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="journal-auth-submit">
                {mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
                {submitting ? "Attendi..." : mode === "login" ? "Accedi" : "Crea profilo"}
              </button>
            </form>
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setAuthError(""); }}
              className="w-full text-center text-sm mt-5 font-semibold" style={{ color: "var(--mm-gold)" }} data-testid="journal-auth-toggle">
              {mode === "login" ? "Non hai un profilo? Creane uno" : "Hai già un profilo? Accedi"}
            </button>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <p className="text-xs uppercase font-bold mb-1" style={{ color: "var(--mm-gold)", letterSpacing: "0.12em", fontFamily: "'Space Grotesk', sans-serif" }}>Profilo Journal</p>
                <p className="text-sm font-semibold" style={{ color: "var(--mm-text-primary)" }} data-testid="journal-user-email">{displayName}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowAdd(!showAdd)} disabled={!activeBookId} className="btn-primary flex items-center gap-2 text-sm" data-testid="add-trade-btn">
                  <Plus size={16} /> Nuovo Trade
                </button>
                <button onClick={() => setShowTrash(!showTrash)}
                  className="relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-white/5"
                  style={{ border: `1px solid ${showTrash ? "var(--mm-gold)" : "var(--mm-border)"}`, color: showTrash ? "var(--mm-gold)" : "var(--mm-text-secondary)" }}
                  data-testid="trash-btn">
                  <Trash2 size={14} /> Cestino
                  {trash.length > 0 && (
                    <span className="text-xs font-bold px-1.5" style={{ backgroundColor: "rgba(239,68,68,0.2)", color: "#EF4444" }} data-testid="trash-count">{trash.length}</span>
                  )}
                </button>
                <button onClick={journalLogout} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-red-500/10"
                  style={{ border: "1px solid rgba(239,68,68,0.4)", color: "#EF4444" }} data-testid="journal-logout-btn">
                  <LogOut size={14} /> Esci dal Journal
                </button>
              </div>
            </div>

            {/* Book tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="journal-books-bar">
              {books.map((b) => {
                const active = b.id === activeBookId;
                if (active && renamingBook) {
                  return (
                    <div key={b.id} className="flex items-center gap-1">
                      <input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && renameBook()}
                        className="admin-input py-1.5 text-sm" style={{ width: "180px" }} data-testid="rename-book-input" />
                      <button onClick={renameBook} className="p-2" style={{ border: "1px solid var(--mm-gold)" }} data-testid="rename-book-save"><Check size={14} style={{ color: "var(--mm-gold)" }} /></button>
                      <button onClick={() => setRenamingBook(false)} className="p-2" style={{ border: "1px solid var(--mm-border)" }}><X size={14} style={{ color: "var(--mm-text-muted)" }} /></button>
                    </div>
                  );
                }
                return (
                  <button key={b.id} onClick={() => setActiveBookId(b.id)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors"
                    style={{
                      border: `1px solid ${active ? "var(--mm-gold)" : "var(--mm-border)"}`,
                      backgroundColor: active ? "rgba(47,216,168,0.12)" : "transparent",
                      color: active ? "var(--mm-gold)" : "var(--mm-text-secondary)",
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                    data-testid={`book-tab-${b.id}`}>
                    {b.name}
                    <span className="text-xs px-1.5" style={{ backgroundColor: active ? "rgba(47,216,168,0.15)" : "rgba(255,255,255,0.06)" }}>{b.trade_count ?? 0}</span>
                  </button>
                );
              })}
              {activeBookId && !renamingBook && (
                <>
                  <button onClick={() => { setRenamingBook(true); setRenameValue(books.find(b => b.id === activeBookId)?.name || ""); }}
                    className="p-2 transition-colors hover:bg-white/5" style={{ border: "1px solid var(--mm-border)" }} title="Rinomina journal" data-testid="rename-book-btn">
                    <Edit3 size={14} style={{ color: "var(--mm-text-muted)" }} />
                  </button>
                  {books.length > 1 && (
                    <button onClick={deleteBook} className="p-2 transition-colors hover:bg-red-500/10" style={{ border: "1px solid rgba(239,68,68,0.35)" }} title="Elimina journal" data-testid="delete-book-btn">
                      <Trash2 size={14} style={{ color: "#EF4444" }} />
                    </button>
                  )}
                </>
              )}
              {showNewBook ? (
                <div className="flex items-center gap-1">
                  <input autoFocus value={newBookName} onChange={(e) => setNewBookName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createBook()}
                    placeholder="Nome journal..." className="admin-input py-1.5 text-sm" style={{ width: "180px" }} data-testid="new-book-input" />
                  <button onClick={createBook} className="p-2" style={{ border: "1px solid var(--mm-gold)" }} data-testid="new-book-save"><Check size={14} style={{ color: "var(--mm-gold)" }} /></button>
                  <button onClick={() => { setShowNewBook(false); setNewBookName(""); }} className="p-2" style={{ border: "1px solid var(--mm-border)" }}><X size={14} style={{ color: "var(--mm-text-muted)" }} /></button>
                </div>
              ) : (
                <button onClick={() => setShowNewBook(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/5"
                  style={{ border: "1px dashed var(--mm-border)", color: "var(--mm-text-muted)" }} data-testid="new-book-btn">
                  <BookPlus size={14} /> Nuovo Journal
                </button>
              )}
            </div>

            {showTrash && (
              <div className="glass-card p-6 mb-8" data-testid="trash-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)", letterSpacing: "0.08em" }}>
                    <Trash2 size={15} style={{ color: "#EF4444" }} /> Cestino — {books.find(b => b.id === activeBookId)?.name}
                  </h3>
                  <span className="text-xs" style={{ color: "var(--mm-text-muted)" }}>Uscendo dal Journal il cestino di tutti i journal si svuota definitivamente</span>
                </div>
                {trash.length === 0 ? (
                  <p className="text-sm text-center py-6" style={{ color: "var(--mm-text-muted)" }} data-testid="trash-empty">Il cestino è vuoto.</p>
                ) : (
                  <div>
                    {trash.map((t) => {
                      const meta = OUTCOMES.find((o) => o.value === t.outcome) || OUTCOMES[0];
                      const pct = parseFloat(t.percentage) || 0;
                      return (
                        <div key={t.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" style={{ borderBottom: "1px solid var(--mm-border)" }} data-testid={`trash-item-${t.id}`}>
                          <span className="text-sm w-28 flex-shrink-0" style={{ color: "var(--mm-text-secondary)" }}>
                            {t.trade_datetime ? new Date(t.trade_datetime).toLocaleDateString("it-IT") : "—"}
                          </span>
                          <span className="text-sm font-semibold w-20 flex-shrink-0" style={{ color: "var(--mm-text-primary)" }}>{t.pair}</span>
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase flex-shrink-0" style={{ color: meta.color, backgroundColor: `${meta.color}1f`, border: `1px solid ${meta.color}55` }}>{meta.label}</span>
                          <span className="text-sm font-bold flex-shrink-0" style={{ color: pct >= 0 ? "#2FD8A8" : "#EF4444" }}>{pct >= 0 ? "+" : ""}{pct}%</span>
                          <span className="flex-1 text-xs truncate" style={{ color: "var(--mm-text-muted)" }}>{t.notes}</span>
                          <button onClick={() => restoreTrade(t.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-white/5 flex-shrink-0"
                            style={{ border: "1px solid var(--mm-gold)", color: "var(--mm-gold)" }}
                            data-testid={`restore-trade-${t.id}`}>
                            <ArchiveRestore size={13} /> Ripristina
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {trades.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Trade Totali", value: trades.length, color: "var(--mm-gold)" },
                  { label: "Win", value: wins, color: "#2FD8A8" },
                  { label: "Loss", value: losses, color: "#EF4444" },
                  { label: "Win Rate", value: winRate != null ? `${winRate}%` : "—", color: "#64C8FF" },
                ].map((s, i) => (
                  <div key={i} className="glass-card p-5 text-center" data-testid={`journal-stat-${i}`}>
                    <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: "'Space Grotesk', sans-serif" }}>{s.value}</p>
                    <p className="text-xs uppercase mt-1" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            <JournalChart trades={trades} />

            <PeriodReview trades={trades} />

            <WhatIf trades={trades} />

            {showAdd && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0, 0, 0, 0.75)", backdropFilter: "blur(4px)" }} onClick={() => { setShowAdd(false); setNewTrade(emptyTrade()); }} data-testid="new-trade-modal-overlay">
                <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8" style={{ backgroundColor: "#0b1512", border: "1px solid var(--mm-gold)" }} onClick={(e) => e.stopPropagation()} data-testid="new-trade-form">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
                      Nuovo Trade — <span className="gold-text">{books.find(b => b.id === activeBookId)?.name}</span>
                    </h3>
                    <button onClick={() => { setShowAdd(false); setNewTrade(emptyTrade()); }} className="p-1.5 transition-colors hover:bg-white/10" style={{ border: "1px solid var(--mm-border)" }} data-testid="close-trade-modal-btn" aria-label="Chiudi">
                      <X size={16} style={{ color: "var(--mm-text-muted)" }} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Data e Orario</label>
                    <input type="datetime-local" value={newTrade.trade_datetime} onChange={(e) => setNewTrade({ ...newTrade, trade_datetime: e.target.value })} className="admin-input" data-testid="new-trade-dt" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Pair</label>
                    <input type="text" value={newTrade.pair} onChange={(e) => setNewTrade({ ...newTrade, pair: e.target.value.toUpperCase() })} placeholder="XAUUSD" className="admin-input" data-testid="new-trade-pair" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Buy / Sell</label>
                    <select value={newTrade.direction} onChange={(e) => setNewTrade({ ...newTrade, direction: e.target.value })} className="admin-input" data-testid="new-trade-direction">
                      {DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Screenshot (link)</label>
                    <input type="text" value={newTrade.screenshot_url} onChange={(e) => setNewTrade({ ...newTrade, screenshot_url: e.target.value })} placeholder="https://..." className="admin-input" data-testid="new-trade-screenshot" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Esito</label>
                    <select value={newTrade.outcome} onChange={(e) => setNewTrade({ ...newTrade, outcome: e.target.value })} className="admin-input" data-testid="new-trade-outcome">
                      {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Percentuale (%)</label>
                    <input type="number" step="0.01" value={newTrade.percentage} onChange={(e) => setNewTrade({ ...newTrade, percentage: e.target.value })} placeholder="es. 1.5 o -1" className="admin-input" data-testid="new-trade-pct" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold block mb-1.5" style={{ color: "var(--mm-text-muted)" }}>Note</label>
                    <input type="text" value={newTrade.notes} onChange={(e) => setNewTrade({ ...newTrade, notes: e.target.value })} placeholder="Setup, emozioni, contesto..." className="admin-input" data-testid="new-trade-notes" />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)" }}>Emotività (errori commessi, se presenti)</label>
                  <div className="flex flex-wrap gap-2" data-testid="new-trade-emotions">
                    {EMOTIONS.map((e) => {
                      const active = newTrade.emotions.includes(e);
                      return (
                        <button key={e} type="button"
                          onClick={() => setNewTrade({ ...newTrade, emotions: active ? newTrade.emotions.filter((x) => x !== e) : [...newTrade.emotions, e] })}
                          className="px-2.5 py-1.5 text-xs font-semibold transition-colors"
                          style={{
                            border: `1px solid ${active ? "#F59E0B" : "var(--mm-border)"}`,
                            color: active ? "#F59E0B" : "var(--mm-text-secondary)",
                            backgroundColor: active ? "rgba(245,158,11,0.1)" : "transparent",
                          }}
                          data-testid={`new-trade-emotion-${e.replace(/\s+/g, "-").toLowerCase()}`}>
                          {e}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={addTrade} className="btn-primary text-sm" data-testid="save-trade-btn">Salva Trade</button>
                  <button onClick={() => { setShowAdd(false); setNewTrade(emptyTrade()); }} className="btn-secondary text-sm" data-testid="cancel-trade-btn">Annulla</button>
                </div>
                </div>
              </div>
            )}

            <TradeTable trades={trades} onUpdate={updateTrade} onDelete={deleteTrade} />
            {trades.length > 0 && (
              <p className="text-xs mt-4" style={{ color: "var(--mm-text-muted)" }}>
                Suggerimento: clicca su una cella per modificarla direttamente. Le modifiche vengono salvate automaticamente.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
